import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Tag, TagGroup, TagList, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type { MetricGroupRep } from '../types';

import styles from './MetricGroupLabel.module.css';

/**
 * Helper component wraps an icon and label in an layout element
 *
 * @param param.icon - the status' icon
 * @param param.label - a descriptive label describing the status
 * @returns
 */
const IconWrapper = ({
  icon,
  label,
  className,
  isMinimal = false,
}: {
  icon?: ReactNode;
  label: string;
  className?: string;
  isMinimal?: boolean;
}) => (
  <TagGroup aria-label="Metric group type" className={clsx(styles.iconWrapper, className)}>
    <TagList>
      <TooltipTrigger>
        <Tag>
          {icon}
          {!isMinimal && <span>{label}</span>}
        </Tag>
        {isMinimal ? <Tooltip>{label}</Tooltip> : null}
      </TooltipTrigger>
    </TagList>
  </TagGroup>
);

export function MetricGroupTypeLabel({
  id,
  className,
  isMinimal = false,
}: {
  id: MetricGroupRep['kind'];
  className?: string;
  isMinimal?: boolean;
}) {
  const iconProps = {
    size: 'small' as IconProps['size'],
  };
  const getIconDetails = useCallback(() => {
    switch (id) {
      case 'funnel':
        return {
          icon: 'metric-funnel' as const,
          label: 'Funnel',
        };
      default:
        return {
          icon: 'metric-group' as const,
          label: 'Standard',
        };
    }
  }, [id]);

  const { icon, label } = getIconDetails();

  return (
    <IconWrapper className={className} icon={<Icon name={icon} {...iconProps} />} label={label} isMinimal={isMinimal} />
  );
}
